import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@structural-climbing/auth'

export interface Trainer {
  user_id: string
  name: string
  email: string
  nickname: string | null
  avatar_ext: string | null
  avatar_updated_at: string | null
  /** People currently assigned to them at this gym. */
  assigned_count: number
  members: AssignedMember[]
}

export interface AssignedMember {
  user_id: string
  name: string
  email: string
  nickname: string | null
  avatar_ext: string | null
  avatar_updated_at: string | null
  /** Set when the trainer coaches this member's child rather than the adult. */
  child_id: string | null
  child_name: string | null
}

/**
 * Everyone who can be picked as a trainer at this gym — anyone holding the
 * `my_members` permission, so a route setter who also coaches appears here
 * without a second invite.
 *
 * `enabled` defers the fetch until the modal actually opens; the members page
 * should not pay for this on every load.
 */
export function useTrainers(enabled = true) {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [trainers, setTrainers] = useState<Trainer[]>([])
  const [loading, setLoading] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const resp = await fetch(`${apiUrl}/trainers`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error(String(resp.status))
      const data = (await resp.json()) as { trainers: Trainer[] }
      setTrainers(data.trainers ?? [])
    } catch {
      setTrainers([])
    } finally {
      setLoading(false)
    }
  }, [apiUrl, getAccessTokenSilently])

  useEffect(() => {
    if (enabled) void reload()
  }, [enabled, reload])

  return { trainers, loading, reload }
}
